import { buildSearchBox, SearchEngine } from '@coveo/headless';
import { Box, ClickAwayListener, Grow, IconButton, Paper, Popper, SearchInputProps } from '@unity/cloud-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import React from 'react';
import { usePathname } from '@/i18n/routing';
import { buildClientSearchEngine } from '@/lib/coveo/engine';
import { BaseSearchInput } from '@/components/search/base-search-input';

export const HeaderSearchInput = (props: { onOpenMobileSearchClick(): void }) => {
  const locale = useLocale();
  const t = useTranslations();
  const pathname = usePathname();

  const searchFieldRef = useRef<HTMLInputElement>(null);
  const [searchEngine, setSearchEngine] = useState<SearchEngine | null>(null);
  const searchBox = useMemo(
    () =>
      searchEngine
        ? buildSearchBox(searchEngine, {
            options: { numberOfSuggestions: 10 },
          })
        : null,
    [searchEngine],
  );
  const [searchState, setSearchState] = useState(searchBox?.state);
  const [searchAnchorEl, setSearchAnchorEl] = React.useState<null | HTMLElement>(null);
  const mobileSearchOpen = Boolean(searchAnchorEl);

  useEffect(() => {
    // Initialize the search engine async to ensure that searchEngine is fully
    // ready before any controllers or subscriptions are set up.
    buildClientSearchEngine(locale).then((engine) => {
      setSearchEngine(engine);
    });
  }, [locale, pathname]);

  useEffect(() => {
    function handleKeyDownWindow(event: KeyboardEvent) {
      const isCmdOrCtrlKeyPressed = (event.ctrlKey || event.metaKey) && event.code === 'KeyK';
      if (isCmdOrCtrlKeyPressed) {
        searchFieldRef.current?.focus();
        event.preventDefault();
      }
    }

    window.addEventListener('keydown', handleKeyDownWindow);
    return () => {
      window.removeEventListener('keydown', handleKeyDownWindow);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = searchBox?.subscribe(() => {
      setSearchState(searchBox?.state);
    });
    return unsubscribe;
  }, [searchBox]);

  useEffect(() => {
    handlePopperSearchInputClose();
    searchBox?.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleMobileSearchClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onOpenMobileSearchClick();
    setSearchAnchorEl(e.currentTarget);
  };

  const handlePopperSearchInputClose = (): void => {
    setSearchAnchorEl(null);
  };

  const renderSearchInput = (props: SearchInputProps & { hideSuggestions?: boolean }) => (
    <BaseSearchInput
      {...props}
      navigateOnSearch
      smallerSuggestions
      searchBoxState={searchState}
      searchFieldRef={searchFieldRef}
      methods={
        searchBox
          ? {
              ...searchBox,
              updateText: (text: string) => {
                searchBox?.updateText(text);
                return;
              },
            }
          : null
      }
      overrideSlotProps
    />
  );

  return (
    <Box position="relative">
      {renderSearchInput({
        id: 'header-search-input',
        sx: { display: { xs: 'none', md: 'block' } },
        inputRef: searchFieldRef,
      })}
      <IconButton
        id="search-button"
        aria-controls={mobileSearchOpen ? 'search-input-popup' : undefined}
        aria-haspopup="true"
        aria-expanded={mobileSearchOpen ? 'true' : undefined}
        aria-label={t('header:search-input:aria-label')}
        color="default"
        sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}
        onClick={handleMobileSearchClick}
      >
        <MagnifyingGlassIcon size={18} aria-hidden="true" focusable="false" />
      </IconButton>
      <Popper
        disablePortal
        transition
        open={mobileSearchOpen}
        anchorEl={searchAnchorEl}
        placement="bottom"
        sx={{ zIndex: 11 }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handlePopperSearchInputClose}>
            <Grow {...TransitionProps}>
              <Paper sx={{ p: 1, width: 250, border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                {renderSearchInput({ id: 'header-search-input-mobile', hideSuggestions: true, autoFocus: true })}
              </Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </Box>
  );
};
